import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);
    document.dispatchEvent(
      new CustomEvent("swym:collections-loaded")
    );
    nextPageItemElements.forEach(card => {
      let variants = JSON.parse(card.querySelector(".plp-actions").getAttribute("data-variants"));
      let optionsArray = JSON.parse(card.querySelector(".plp-actions").getAttribute("data-options-array"));

      productUpdate(variants, optionsArray, card);

      // swatches and colors
      const swatches = card.querySelectorAll(".swatches-container .swatch-item");
      swatches.forEach(swatch => {
        swatch.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();

          swatches.forEach(swatchItem => swatchItem.style.outline = "none");
          swatch.style.outline = "1px solid #d8d7d1";
          swatch.style.borderRadius = "50%";

          let getVariants = JSON.parse(swatch.getAttribute("data-variants"));
          let getOptions = JSON.parse(swatch.getAttribute("data-options-array"));
          let productMedia = JSON.parse(swatch.getAttribute("data-product-media"));
          let productInfo = JSON.parse(swatch.getAttribute("data-product-info"));
          productInfo.badges = swatch.getAttribute("data-product-badge");
          productInfo.discount = swatch.getAttribute("data-product-discount");
          productUpdate(getVariants, getOptions, card);
          productImageAndInfoUpdate(card, productMedia, productInfo)
        })
      })



    });

    function sizeItems(actionsContainer, card) {
      actionsContainer.querySelectorAll(".size-item").forEach(actionItem => {
        actionItem.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          actionsContainer.querySelectorAll(".size-item").forEach(item => item.classList.remove("active-size"))
          actionItem.classList.add("active-size");
          card.querySelector(".my-custom-wishlist-button").setAttribute(
            'data-variant-id',
            actionItem.getAttribute('data-variant-id')
          );
          document.dispatchEvent(new CustomEvent("swym:collections-loaded"));
        })
      })

      actionsContainer.querySelector(".add-to-bag").addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();

        let activeSize = actionsContainer.querySelector(".active-size");
        if (activeSize) {
          let variantId = activeSize.dataset.variantId;
          fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              id: variantId,
              quantity: 1
            })
          })
            .then(res => res.json())
            .then(cart => {
              document.dispatchEvent(
                new CustomEvent('cart:update', {
                  bubbles: true,
                  detail: {
                    data: {
                      itemCount: 1,
                      source: 'product-form-component'
                    }
                  }
                })
              );
            })
            .catch(err => {
              console.error("Error:", err);
            });
        }
      })
    }

    function productUpdate(variants, optionsArray, card) {

      const sizeIndex = optionsArray.findIndex(opt =>
        opt.toLowerCase().includes('size')
      );

      if (sizeIndex === -1) return;

      const actionsContainer = card.querySelector(".plp-actions");

      actionsContainer.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
      });

      actionsContainer.querySelector(".plp-sizes")?.remove();
      actionsContainer.querySelector(".add-to-bag")?.remove();

      const sizeWrapper = document.createElement("div");
      sizeWrapper.classList.add("plp-sizes");


      let firstAvailableAssigned = false;

      variants.forEach(variant => {
        const size = variant[`option${sizeIndex + 1}`];
        const sizeElement = document.createElement("span");
        sizeElement.textContent = size;
        sizeElement.classList.add("size-item");
        sizeElement.dataset.variantId = variant.id;
        sizeElement.dataset.available = variant.available;

        if (variant.available && !firstAvailableAssigned) {
          sizeElement.classList.add("active-size");
          firstAvailableAssigned = true;
        }

        sizeWrapper.appendChild(sizeElement);
      })

      actionsContainer.appendChild(sizeWrapper);

      let addButton = document.createElement("button");
      addButton.classList.add("add-to-bag");
      addButton.textContent = "Add to Bag";

      actionsContainer.appendChild(addButton);

      sizeItems(actionsContainer, card);
    }

    function productImageAndInfoUpdate(card, productMedia, productInfo) {
      const slides = card.querySelectorAll(
        ".card-gallery slideshow-component slideshow-container slideshow-slides slideshow-slide"
      );

      for (let i = 0; i < 2; i++) {
        const img = slides[i]?.querySelector('.product-media img');
        if (img && productMedia[i]) {
          img.src = productMedia[i];
          img.srcset = productMedia[i];
        }
      }


      // For Product Title, Handle and Price update
      card.querySelector('a[ref="productTitleLink"] p[role="heading"]').textContent = productInfo.title;
      card.querySelector('a[ref="productTitleLink"]').setAttribute("href", `/products/${productInfo.handle}`);
      card.querySelector('a[ref="cardGalleryLink"]').setAttribute("href", `/products/${productInfo.handle}`);
      if (productInfo.compare_at_price > 0) {
        card.querySelector('product-price div[ref="priceContainer"] .price__regular').classList.add('price__hidden');
        card.querySelector('product-price div[ref="priceContainer"] .price__sale').classList.remove('price__hidden');
        card.querySelector('product-price div[ref="priceContainer"] .price__sale .price-item--sale.price').textContent = (productInfo.price / 100).toFixed(2);
        card.querySelector('product-price div[ref="priceContainer"] .price__sale .price-item--regular.compare-at-price').textContent = window.formatMoney(productInfo.compare_at_price);
      }
      else {
        card.querySelector('product-price div[ref="priceContainer"] .price__regular').classList.remove('price__hidden');
        card.querySelector('product-price div[ref="priceContainer"] .price__sale').classList.add('price__hidden');
        card.querySelector('product-price div[ref="priceContainer"] .price__regular .price').textContent = window.formatMoney(productInfo.price);
      }

      const garmentImage = card.querySelector('.product-media__garment_image');
      if (garmentImage) {
        const productMedia = garmentImage.closest('.product-media');
        if (productMedia.getAttribute('data-garment-image')) {
          if (productMedia.getAttribute('data-garment-image') == "-0") {
            garmentImage.src = productInfo.media[productInfo.media.length - 1].src
            garmentImage.srcset = productInfo.media[productInfo.media.length - 1].src
          } else if (productMedia.getAttribute('data-garment-image') == "-1") {
            garmentImage.src = productInfo.media[productInfo.media.length - 2].src
            garmentImage.srcset = productInfo.media[productInfo.media.length - 2].src
          } else {
            garmentImage.src = productInfo.media[productMedia.getAttribute('data-garment-image')].src
            garmentImage.srcset = productInfo.media[productMedia.getAttribute('data-garment-image')].src
          }
        }
      }

      let getProductBadges = card.querySelector(".product-badges");
      getProductBadges.innerHTML = ``;
      if (getProductBadges && productInfo.tags?.length) {
        let badgeText = '';

        if (productInfo.tags.includes('Final Sale')) {
          badgeText = 'Final Sale';
        } else if (productInfo.tags.includes('Sale')) {
          badgeText = 'Sale';
        }

        if (badgeText) {
          getProductBadges.innerHTML = `
      <div class="product-badges__badge product-badges__badge--rectangle sale-badge">
        ${badgeText}
      </div>
    `;
        }
      }
      if (productInfo.badges) {
        getProductBadges.innerHTML += `
      <div class="product-badges__badge product-badges__badge--rectangle sale-badge">
        ${productInfo.badges}
      </div>
    `;
      }

      if (productInfo.discount) {
        getProductBadges.innerHTML += `
      <div class="product-badges__badge product-badges__badge--rectangle discount-badge">
        ${productInfo.discount}
      </div>
    `;
      }
      const wishlistButton = card.querySelector('.my-custom-wishlist-button');

      if (wishlistButton) {
        const selectedVariant = productInfo.variants.find(v => v.available) || productInfo.variants[0];

        wishlistButton.setAttribute('data-product-id', productInfo.id);
        wishlistButton.setAttribute('data-variant-id', selectedVariant.id);
        wishlistButton.setAttribute(
          'data-product-url',
          `${window.location.origin}/products/${productInfo.handle}`
        );
        wishlistButton.classList.forEach(className => {
          if (className.startsWith('product_')) {
            wishlistButton.classList.remove(className);
          }
        });
        wishlistButton.classList.add(`product_${productInfo.id}`);

        // Reset state so Swym can re-evaluate
        wishlistButton.classList.remove('swym-added');
        wishlistButton.setAttribute('aria-pressed', 'false');
        document.dispatchEvent(new CustomEvent("swym:collections-loaded"));

      }

    }


    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"], :scope > li.results-grid__tile');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
